import { Children, useCallback, useEffect, useRef, useState } from "react";
import { Carousel } from "@/components/site/primitives";
import BannerDots from "@/components/site/BannerDots";
import { useSite } from "@/context/SiteContext";
import { DOTS_DEFAULTS } from "@/lib/sectionVariants";

const PAUSE_MS = 6000;

export const carouselOptions = (section) => ({
  showDots: section?.key === "banners" ? section?.dots?.enabled !== false : section?.show_dots !== false,
  autoplay: section?.autoplay !== false,
  interval: Number(section?.autoplay_seconds) || 4,
});

const AutoCarousel = ({ children, className = "", section, dotsInside = false, testId }) => {
  const ref = useRef(null);
  const pauseUntil = useRef(0);
  const [pages, setPages] = useState(1);
  const [active, setActive] = useState(0);
  const { sections } = useSite();
  const style = { ...DOTS_DEFAULTS, ...(sections.find((s) => s.key === "banners")?.dots || {}) };
  const { showDots, autoplay, interval } = carouselOptions(section);
  const count = Children.count(children);

  const measure = useCallback(() => {
    const el = ref.current;
    if (!el || !el.clientWidth) return;
    const p = Math.max(1, Math.ceil((el.scrollWidth - 4) / el.clientWidth));
    const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
    setPages(p);
    setActive(atEnd ? p - 1 : Math.min(p - 1, Math.round(el.scrollLeft / el.clientWidth)));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    return () => ro.disconnect();
  }, [measure, count]);

  useEffect(() => {
    if (!autoplay) return undefined;
    const id = setInterval(() => {
      const el = ref.current;
      if (!el || document.hidden || Date.now() < pauseUntil.current) return;
      if (el.scrollWidth <= el.clientWidth + 4) return;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
      const [first, second] = el.children;
      const step = second ? second.offsetLeft - first.offsetLeft : el.clientWidth;
      el.scrollTo({ left: atEnd ? 0 : el.scrollLeft + step, behavior: "smooth" });
    }, Math.max(2, interval) * 1000);
    return () => clearInterval(id);
  }, [autoplay, interval]);

  const pause = () => { pauseUntil.current = Date.now() + PAUSE_MS; };
  const goTo = (p) => {
    const el = ref.current;
    if (!el) return;
    pause();
    el.scrollTo({ left: p >= pages - 1 ? el.scrollWidth : p * el.clientWidth, behavior: "smooth" });
    setActive(p);
  };

  const dots = showDots && pages > 1 && (
    <BannerDots count={pages} active={active} onSelect={goTo} config={style} testId={testId} />
  );

  return (
    <div className="relative" data-testid={testId ? `${testId}-carousel` : undefined}>
      <Carousel ref={ref} className={className} onScroll={measure} onPointerDown={pause} onTouchStart={pause} onWheel={pause}>
        {children}
      </Carousel>
      {dots && dotsInside && (
        <div className="absolute top-3 left-7 sm:left-3 pointer-events-none [&>div]:pointer-events-auto [&>div]:bg-black/30 [&>div]:backdrop-blur [&>div]:rounded-full [&>div]:px-2 [&>div]:py-1.5">{dots}</div>
      )}
      {dots && !dotsInside && <div className="mt-2">{dots}</div>}
    </div>
  );
};

export default AutoCarousel;
